import React, { useEffect, useMemo, useRef, useState } from 'react';
import { BTN, BTN_XS } from '../../constants/buttons';
import { diffChangedFields } from '../../utils/formDiff';
import { dateOnly } from '../../utils/formDiff';
import { FIELD_CLS, LABEL_CLS } from '../gags/OwnerSubDeptFields';
import UserAutocomplete from '../UserAutocomplete';
import PhoneActionBar from '../ui/PhoneActionBar';
import NumberInput from '../ui/NumberInput';
import { unreadableDaysProblem, DAYS_PROBLEM_CLASS } from '../../utils/daysEntry';
import {
  TASK_STATUSES,
  TASK_STATUS_LABELS,
  TASK_PRIORITIES,
  TASK_PRIORITY_LABELS,
} from '../../constants/taskStatuses';

/** Editable task form seeded from a task row (all values as strings). */
function taskFormFromTask(t) {
  return {
    title: t.title || '',
    description: t.description || '',
    status: t.status || 'todo',
    priority: t.priority || 'medium',
    start_date: dateOnly(t.start_date),
    due_date: dateOnly(t.due_date),
    estimated_days: t.estimated_days != null ? String(t.estimated_days) : '',
    actual_days: t.actual_days != null ? String(t.actual_days) : '',
    sub_department_id: t.sub_department_id != null ? String(t.sub_department_id) : '',
    assignee_ids: (t.assignees || []).map((a) => a.user_id),
  };
}

const sortedIds = (ids) => [...ids].sort((a, b) => a - b).join(',');

/**
 * Shared editable body for a task — endpoint-agnostic. Task vocabulary ONLY
 * (no 'ready' — that is gag-only). Comments/files affordances are OPTIONAL:
 * a caller with per-task comment/file endpoints (the team surface) passes
 * `taskExtras`; team-less (gag) callers omit it and NOTHING extra renders, so
 * the gag editor stays byte-identical.
 *
 * Used by BOTH the inline List-view row expansion (mode='inline') and the
 * GagTaskModal popup (mode='modal') — the form + save/delete logic live here
 * once and are never forked. The network is injected: the editor computes the
 * diff and hands it to the caller's onSave/onDelete, so the same form can serve
 * gag tasks (/api/gags/...) or team tasks (/api/teams/...) unchanged.
 *
 * Props:
 *  - task
 *  - canEdit: gag can_edit OR task creator (mirrors the server guard) — when
 *    false the whole form is read-only (no Save/Delete/Cancel).
 *  - subDepts, assignableUsers — subDepts empty hides the sub-department field
 *    (team callers have no sub-departments → clean form).
 *  - mode: 'inline' | 'modal' — footer arrangement only (see render).
 *  - onSave(patch) => Promise<Response|void>: caller performs the PUT with the
 *    computed diff and returns the response (or throws). The editor keeps the
 *    same success/error handling it had when the call was inline.
 *  - onDelete() => Promise<Response|void>: caller performs the DELETE and
 *    returns the response (or throws).
 *  - onSaved(): after a successful save (parent does the silent refetch;
 *    modal also closes on this).
 *  - onDeleted(): after a successful delete.
 *  - onCancel(): modal Cancel/Close (unused inline — inline Cancel reverts).
 *  - onDirtyChange(bool): report unsaved-changes state (inline dirty guard).
 *  - showDirtyWarning: parent's blocked-collapse signal (pulse amber).
 *  - onBusyChange(bool): report in-flight save/delete (modal Escape/backdrop
 *    guard).
 *  - taskExtras(task) => ReactNode (OPTIONAL): rendered below the form footer
 *    (e.g. a comments thread + file uploads for team tasks). Omitted on the gag
 *    surface → nothing renders. Always shown (edit-independent) so read-only
 *    viewers can still read comments / open attachments.
 */
export default function TaskEditor({
  task,
  canEdit,
  subDepts,
  assignableUsers,
  mode = 'inline',
  onSave,
  onDelete,
  onSaved,
  onDeleted,
  onCancel,
  onDirtyChange,
  showDirtyWarning,
  onBusyChange,
  taskExtras,
  // ── Gags/teams/tasks Stage 1 (2026-10-02) — both OPTIONAL; omitted, the
  // editor is exactly what it was. ──
  // canAssign: may this caller change the ASSIGNEES (rule 3: gag owner or
  // breakdown rights; rule 5 for a no-gag task)? false → the assignee list is
  // read-only while the other fields follow `canEdit`. The server re-checks.
  canAssign = true,
  // assignPicker: { options: [{ id, display_name, status, group }] } → the
  // assignees render as chips with a `UserAutocomplete` to add one (the gag's
  // TEAM listed first, then the department — spec "Assigning a task"),
  // replacing the checkbox list.
  assignPicker = null,
  // readOnlyNote: the sentence shown when `canEdit` is false.
  readOnlyNote = ' — only a gag editor or the task creator can change this task.',
  // OPTIONAL (2026-10-07, ask-then-continue — owner: "A, convert all three").
  // The OrderForm `submitRef` device, so a parent's three-way prompt can act on
  // THIS editor without a second implementation of either action:
  //  - saveRequestRef.current = () => Promise<boolean> — runs `save` below (the
  //    Save button's ONE path). true only after a successful save, or when
  //    nothing is unsaved; false on any refusal or failed request, so the
  //    parent never runs a held click over a save that did not land.
  //  - discardRequestRef.current = () => void — runs `revert` (inline Cancel).
  // Omitted, the editor is exactly what it was.
  // 2026-10-18: `saveRequestRef.current({ onError })` also hands a refusal's
  // message to `onError(message)` — the SAME string this editor shows — so a
  // parent saving gag + task can say exactly which half failed and why.
  saveRequestRef = null,
  discardRequestRef = null,
  // OPTIONAL (2026-10-18, owner): true under an editing DESKTOP Breakdown row,
  // whose bar carries the ONE "Unsaved changes" chip. Save task / Discard still
  // appear only while dirty — that is this editor's local signal.
  hideDirtyChip = false,
}) {
  const [form, setForm] = useState(() => taskFormFromTask(task));
  const [original, setOriginal] = useState(() => taskFormFromTask(task));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const set = (field) => (e) => setForm((p) => ({ ...p, [field]: e.target.value }));
  const setValue = (field) => (v) => setForm((p) => ({ ...p, [field]: v }));
  const estimatedProblem = unreadableDaysProblem('Estimated days', form.estimated_days);
  const actualProblem = unreadableDaysProblem('Actual days', form.actual_days);

  const toggleAssignee = (uid) => {
    setForm((p) => ({
      ...p,
      assignee_ids: p.assignee_ids.includes(uid)
        ? p.assignee_ids.filter((id) => id !== uid)
        : [...p.assignee_ids, uid],
    }));
  };

  // Union of pickable users + any current assignees no longer in the pickable
  // list (e.g. deactivated since assignment) so they stay visible/removable.
  const assigneeOptions = useMemo(() => {
    const opts = assignableUsers.map((u) => ({ id: u.id, name: u.display_name }));
    const known = new Set(opts.map((o) => o.id));
    for (const a of task.assignees || []) {
      if (!known.has(a.user_id)) opts.push({ id: a.user_id, name: a.display_name });
    }
    return opts;
  }, [assignableUsers, task.assignees]);

  // ── Dirty tracking (all-strings invariant → strict !==; assignees compared
  //    order-independently since toggling reorders the array). ──
  const isDirty = useMemo(() => {
    for (const k of Object.keys(form)) {
      if (k === 'assignee_ids') continue;
      if (form[k] !== original[k]) return true;
    }
    return sortedIds(form.assignee_ids) !== sortedIds(original.assignee_ids);
  }, [form, original]);

  // Report dirty/busy up (ref-stabilised so a changing callback identity
  // doesn't re-fire the effect); clear the dirty flag on unmount so a
  // collapsed/closed editor can't leave a stale guard behind.
  const onDirtyChangeRef = useRef(onDirtyChange);
  useEffect(() => { onDirtyChangeRef.current = onDirtyChange; });
  useEffect(() => { onDirtyChangeRef.current?.(isDirty); }, [isDirty]);
  useEffect(() => () => onDirtyChangeRef.current?.(false), []);

  const onBusyChangeRef = useRef(onBusyChange);
  useEffect(() => { onBusyChangeRef.current = onBusyChange; });
  useEffect(() => { onBusyChangeRef.current?.(busy); }, [busy]);

  // Resolves true when saved (or nothing to save), false on any refusal —
  // `saveRequestRef` below reads it; the Save buttons ignore it.
  const save = async ({ onError } = {}) => {
    // Every refusal goes through here, so the parent hears the message shown.
    const fail = (message) => { setError(message); onError?.(message); return false; };
    if (busy) return false;
    if (!form.title.trim()) return fail('Task title is required');
    // Unreadable days must not CLEAR a stored value (input audit #6) — refused by name.
    const daysError = estimatedProblem || actualProblem;
    if (daysError) return fail(daysError);

    // Only send changed fields — resending an unchanged 'done' status would
    // re-stamp completed_date to today on the server.
    const payload = diffChangedFields(form, original, {
      skip: ['assignee_ids'],
      trimFields: ['title'],
    });
    if (sortedIds(form.assignee_ids) !== sortedIds(original.assignee_ids)) {
      payload.assignee_ids = form.assignee_ids;
    }
    if (Object.keys(payload).length === 0) {
      // Nothing changed. Modal closes (its Save doubles as Done); inline can't
      // reach here (its Save renders only while dirty).
      if (mode === 'modal') onCancel?.();
      return true;
    }

    setBusy(true);
    setError(null);
    try {
      // Caller performs the PUT and returns the response (or throws).
      const res = await onSave(payload);
      const data = res ? await res.json().catch(() => ({})) : {};
      if (res && !res.ok) {
        setBusy(false);
        return fail(data.error || `Failed to save task (HTTP ${res.status})`);
      }
      // The PUT succeeded — the form is no longer "unsaved". Reseed the
      // snapshot so an inline editor that stays mounted goes clean; the parent
      // then silently refetches the list/detail.
      setOriginal(form);
      setBusy(false);
      onSaved?.();
      return true;
    } catch {
      setBusy(false);
      return fail('Failed to save task — check your connection.');
    }
  };

  const remove = async () => {
    if (busy) return;
    if (!window.confirm(`Delete task "${task.title}"?`)) return;
    setBusy(true);
    setError(null);
    try {
      // Caller performs the DELETE and returns the response (or throws).
      const res = await onDelete();
      if (res && !res.ok) {
        const data = await res.json().catch(() => ({}));
        setError(data.error || 'Failed to delete task');
        setBusy(false);
        return;
      }
      setBusy(false);
      onDeleted?.();
    } catch {
      setError('Failed to delete task — check your connection.');
      setBusy(false);
    }
  };

  // Inline Cancel reverts to the last-saved snapshot (matches the gag editor's
  // Cancel); modal Cancel closes (handled by onCancel in the footer).
  const revert = () => { setForm(original); setError(null); };

  // Published every render so each always closes over the current form; the
  // cleanup clears a ref only while it still holds THIS editor's function.
  useEffect(() => {
    if (!saveRequestRef) return undefined;
    const request = (opts) => (isDirty ? save(opts) : Promise.resolve(true));
    saveRequestRef.current = request;
    return () => { if (saveRequestRef.current === request) saveRequestRef.current = null; };
  });
  useEffect(() => {
    if (!discardRequestRef) return undefined;
    discardRequestRef.current = revert;
    return () => { if (discardRequestRef.current === revert) discardRequestRef.current = null; };
  });

  const pulse = showDirtyWarning && isDirty ? 'animate-pulse' : '';

  return (
    <div className="space-y-3">
      {/* From `md` up an inline editor's Save/Discard sit in a header row at its
          TOP (owner 2026-10-07: "do the same on tasks for desktop") — the gag
          panel's header pair, same order and size. A phone keeps the pinned
          bottom bar below. */}
      {mode !== 'modal' && canEdit && isDirty && (
        <div className="hidden md:flex items-center gap-2" data-testid="task-editor-header-actions">
          {!hideDirtyChip && (
            <span className={`text-xs font-medium text-amber-600 dark:text-amber-400 ${pulse}`}>
              &#9888; Unsaved changes
            </span>
          )}
          <button type="button" onClick={revert} disabled={busy}
            title="Discard unsaved edits" className={`${BTN_XS.secondary} ${pulse} ml-auto`}>
            Discard
          </button>
          <button type="button" onClick={save} disabled={busy || !form.title.trim()}
            title="Save the changed task fields" className={`${BTN_XS.confirm} ${pulse}`}>
            {busy ? 'Saving…' : 'Save task'}
          </button>
        </div>
      )}
      {error && (
        <div className="px-3 py-2 text-sm rounded-lg bg-red-50 text-red-700 border border-red-200 dark:bg-red-900/30 dark:text-red-300 dark:border-red-800">
          {error}
        </div>
      )}

      <div>
        <label className={LABEL_CLS}>Title *</label>
        <input type="text" value={form.title} onChange={set('title')} maxLength={200}
          disabled={busy || !canEdit} className={FIELD_CLS} />
      </div>

      <div>
        <label className={LABEL_CLS}>Description</label>
        <textarea value={form.description} onChange={set('description')} rows={3}
          disabled={busy || !canEdit} className={`${FIELD_CLS} resize-y`} />
      </div>

      <div className="grid grid-cols-2 gap-3">
        <div>
          <label className={LABEL_CLS}>Status</label>
          <select value={form.status} onChange={set('status')} disabled={busy || !canEdit} className={FIELD_CLS}>
            {TASK_STATUSES.map((s) => (
              <option key={s} value={s}>{TASK_STATUS_LABELS[s]}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={LABEL_CLS}>Priority</label>
          <select value={form.priority} onChange={set('priority')} disabled={busy || !canEdit} className={FIELD_CLS}>
            {TASK_PRIORITIES.map((p) => (
              <option key={p} value={p}>{TASK_PRIORITY_LABELS[p]}</option>
            ))}
          </select>
        </div>
        <div>
          <label className={LABEL_CLS}>Start date</label>
          <input type="date" value={form.start_date} onChange={set('start_date')}
            disabled={busy || !canEdit} className={FIELD_CLS} />
        </div>
        <div>
          <label className={LABEL_CLS}>Due date</label>
          <input type="date" value={form.due_date} onChange={set('due_date')}
            disabled={busy || !canEdit} className={FIELD_CLS} />
        </div>
        <div>
          <label className={LABEL_CLS}>Estimated days</label>
          <NumberInput min="0.25" step="0.25" placeholder="e.g. 1.5" value={form.estimated_days}
            onChange={setValue('estimated_days')} disabled={busy || !canEdit} className={FIELD_CLS} testId="task-editor-estimated-days" />
          {estimatedProblem && <p className={DAYS_PROBLEM_CLASS} role="alert" data-testid="task-editor-estimated-days-problem">{estimatedProblem}</p>}
        </div>
        <div>
          <label className={LABEL_CLS}>Actual days</label>
          <NumberInput min="0.25" step="0.25" placeholder="e.g. 0.75" value={form.actual_days}
            onChange={setValue('actual_days')} disabled={busy || !canEdit} className={FIELD_CLS} testId="task-editor-actual-days" />
          {actualProblem && <p className={DAYS_PROBLEM_CLASS} role="alert" data-testid="task-editor-actual-days-problem">{actualProblem}</p>}
        </div>
      </div>

      {subDepts.length > 0 && (
        <div>
          <label className={LABEL_CLS}>Sub-department</label>
          <select value={form.sub_department_id} onChange={set('sub_department_id')}
            disabled={busy || !canEdit} className={FIELD_CLS}>
            <option value="">None</option>
            {subDepts.map((sd) => (
              <option key={sd.id} value={sd.id}>{sd.name}</option>
            ))}
          </select>
        </div>
      )}

      {assignPicker ? (
        <div data-testid="task-editor-assignees">
          <label className={LABEL_CLS}>Assigned to</label>
          <div className="flex flex-wrap items-center gap-1.5 mb-1.5">
            {form.assignee_ids.length === 0 && (
              <span className="text-xs text-gray-500 dark:text-gray-400">Nobody yet</span>
            )}
            {form.assignee_ids.map((uid) => {
              const name = (assignPicker.options.find((o) => o.id === uid) || {}).display_name
                || ((task.assignees || []).find((a) => a.user_id === uid) || {}).display_name
                || `User ${uid}`;
              return (
                <span key={uid} className="inline-flex items-center gap-1 pl-2 pr-1 py-0.5 rounded-full bg-blue-50 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200 text-xs">
                  {name}
                  {canEdit && canAssign && (
                    <button type="button" onClick={() => toggleAssignee(uid)} disabled={busy}
                      aria-label={`Unassign ${name}`}
                      className="w-5 h-5 inline-flex items-center justify-center rounded-full hover:bg-blue-100 dark:hover:bg-blue-800/50">
                      <span aria-hidden="true">✕</span>
                    </button>
                  )}
                </span>
              );
            })}
          </div>
          {canEdit && canAssign ? (
            <UserAutocomplete
              value=""
              options={assignPicker.options}
              excludeUserIds={form.assignee_ids}
              clearOnSelect
              ariaLabel="Assign someone"
              placeholder="Assign someone…"
              onChange={(_name, uid) => { if (uid != null && !form.assignee_ids.includes(uid)) toggleAssignee(uid); }}
            />
          ) : canEdit ? (
            <p className="text-xs text-gray-500 dark:text-gray-400">Only the gag owner or a breakdown manager can change who this is assigned to.</p>
          ) : null}
        </div>
      ) : (
      <div>
        <label className={LABEL_CLS}>Assignees</label>
        {assigneeOptions.length === 0 ? (
          <p className="text-xs text-gray-400 dark:text-gray-500 italic">No assignable users in this department.</p>
        ) : (
          <div className="max-h-36 overflow-y-auto rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 p-2 space-y-1">
            {assigneeOptions.map((u) => (
              <label key={u.id} className="flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200 cursor-pointer">
                <input
                  type="checkbox"
                  checked={form.assignee_ids.includes(u.id)}
                  onChange={() => toggleAssignee(u.id)}
                  disabled={busy || !canEdit || !canAssign}
                  className="rounded border-gray-300 dark:border-gray-600"
                />
                {u.name}
              </label>
            ))}
          </div>
        )}
      </div>
      )}

      <p className="text-xs text-gray-400 dark:text-gray-500">
        Created by {task.created_by_name || 'unknown'}
        {!canEdit && readOnlyNote}
      </p>

      {canEdit && (
        mode === 'modal' ? (
          <div className="flex items-center gap-2 pt-1">
            <button type="button" onClick={remove} disabled={busy} className={BTN['danger-soft']}>
              Delete
            </button>
            <div className="ml-auto flex gap-2">
              <button type="button" onClick={() => onCancel?.()} disabled={busy} className={BTN.secondary}>
                Cancel
              </button>
              <button type="button" onClick={save} disabled={busy || !form.title.trim()} className={BTN.confirm}>
                {busy ? 'Saving…' : 'Save'}
              </button>
            </div>
          </div>
        ) : (
          // Inline (owner 2026-10-05, the order edit's rule): no in-flow Save /
          // Cancel — they appear only once something has changed, CENTRED and
          // pinned to the bottom on a PHONE (`ui/PhoneActionBar`); from `md` up
          // they are the header row above (2026-10-07). Delete stays in flow:
          // it is always available. The modal keeps its footer.
          <>
            <div className="flex items-center gap-2 pt-1">
              <button type="button" onClick={remove} disabled={busy}
                title="Delete this task" className={`${BTN['danger-soft']} ml-auto`}>
                Delete
              </button>
            </div>
            {isDirty && (
              <PhoneActionBar pinned className="flex items-center justify-center gap-2 md:hidden" testId="task-editor-actions">
                <button type="button" onClick={save} disabled={busy || !form.title.trim()}
                  title="Save the changed task fields" className={`${BTN.confirm} ${pulse}`}>
                  {busy ? 'Saving…' : 'Save task'}
                </button>
                <button type="button" onClick={revert} disabled={busy}
                  title="Discard unsaved edits" className={`${BTN.secondary} ${pulse}`}>
                  Cancel
                </button>
              </PhoneActionBar>
            )}
          </>
        )
      )}

      {/* Optional per-task extras (team comments + files). Absent on the gag
          surface → nothing renders. Shown regardless of canEdit so read-only
          viewers can still read the thread / open attachments. */}
      {taskExtras && (
        <div className="pt-4 mt-1 border-t border-gray-200 dark:border-gray-600 space-y-5">
          {taskExtras(task)}
        </div>
      )}
    </div>
  );
}
